import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { EventsState } from '../../../../../../store/types';
import { CallToAction, ColorKeys, SessionTypesEnum, TextOverrides } from '../../../../../../types/working-model';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import Modal from '../../../../../general-ui/modal/modal';
import { UploadImageModal } from "../../../../../general-ui/image-upload-modal/image-upload-modal";
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import Controls, { InitialControlsDisplay } from '../../../session/editor/moduleControls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { getStartTime } from '../../../../../../utils/get-event-start-time';
import { Countdown } from '../../../../../general-ui/countdown/countdown';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import CallToActionForm from '../../../../../general-ui/call-to-action/call-to-action';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { Button } from '../../../../../general-ui/button/button';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { BasicLangingModuleProps } from '../editor-main';
import '../../../../../live-event/modules/main-event-banner/main-event-banner.scss';
import './main-event-banner.scss';

// React.forwardRef is used to access refs in children
const EditorMainEventBanner: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const {
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: {
			token,
			user
		}
	} = useSelector((event: EventsState) => event);

	const content = page_module?.content;

	const [image, setImage] = useState<{ original: string, edited: string; }>({ original: content.originalImage, edited: content.image });
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [callToActionOpen, setCallToActionOpen] = useState(false);
	const [workingCTA, setWorkingCTA] = useState<CallToAction | null>(page_module?.content?.callToAction);

	const dispatch = useDispatch();

	const { t } = useTranslation('homepage');

	const layout_type = content?.layout_type || "standard";
	const hasBroadcastEvent = useMemo(() => workingEvent?.sessions?.some(session => session.timestamp), [workingEvent]);
	const startDate = workingEvent && hasBroadcastEvent ? new Date(getStartTime(workingEvent, false)!) : null;
	const startsOnDate = startDate ? startDate.toLocaleString() : "";
	const styling_overrides = page_module?.styling_overrides;
	//check if the property exists yet
	const countdownIsOn = ("countdownIsOn" in content)
		? content.countdownIsOn
		// backward compatable: check if any session has a broadcast date, then turn on
		: workingEvent?.sessions?.some(session => session.session_type === SessionTypesEnum.broadcast);

	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);


	function setHomepageTitle(title: string) {
		dispatch(
			updateHomepageModule(
				{
					...page_module,
					content: {
						...content,
						title: updateTranslateKey({
							translateString: page_module?.content?.title,
							input: title,
							language,
							baseLanguage
						})
					}
				}
			)
		);
	}

	function handleDescriptionChange(text: string) {
		dispatch(
			updateHomepageModule(
				{
					...page_module,
					content: {
						...content,
						description: updateTranslateKey({
							translateString: page_module?.content?.description,
							input: text,
							language,
							baseLanguage
						})
					}
				}
			)
		);

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing event description
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editDescription)
		) {
			dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editDescription));
		}
	}

	function openImageModal() {
		setImageModalOpen(true);
	}

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...page_module.content,
					originalImage: image.original,
					image: image.edited,
				}
			})
		);
		setImage(image);
	}, [dispatch, page_module]);

	function editButton() {
		setCallToActionOpen(true);
	}

	function cancelEditButton() {
		setCallToActionOpen(false);
		setWorkingCTA(null);
	}

	function setCallToActionButton() {

		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...content,
					callToAction: workingCTA
				}
			})
		);

		setCallToActionOpen(false);
		setWorkingCTA(null);

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing the button name to Register to Event
		if (
			user &&
			!user?.onboarded.includes(ChecklistTitles.editButtonName) &&
			workingCTA?.button_name.base === "Register to Event"
		) {
			dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editButtonName));
		}
	}

	function handleTitleOverrides(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				title: overrides
			}
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides
			})
		);
	}

	function handleDescriptionOverrides(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				description: overrides
			}
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides
			})
		);
	}

	const displayBannerControls: InitialControlsDisplay = {
		editLayout: true,
		editColors: true,
		changeSectionPosition: true,
	};

	const { description, title } = getTextValue(content, language);
	return workingEvent && page_module.is_on ? (
		<section
			ref={ref}
			className={classNames(
				"editor-wrapper main-event-banner",
				`main-event-banner-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				display={displayBannerControls}
				openEditModal={() => 'leave blank if not editing content'}
				module={page_module}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="banner-title-container">

					{/* Don't display the element when there is no text because it gets styled in some templates */}
					{startsOnDate &&
						<TypographyItem className="evt-caption-1 banner-starts-on" tagName="p">
							{t("Starts on") + " " + startsOnDate}
						</TypographyItem>
					}
					<EditableTextSlim
						tagType="h1"
						value={title}
						className="banner-title"
						onChange={setHomepageTitle}
						colorOverride={styling_overrides?.content?.title?.color}
						onOverrideChange={handleTitleOverrides}
						defaultColor={ColorKeys.headingTextColor}
						elementName={EContentColorOverrideNames.title}
					/>

					<EditableTextDraft
						value={description}
						className="banner-description"
						onChange={handleDescriptionChange}
						colorOverride={styling_overrides?.content?.description?.color}
						onOverrideChange={handleDescriptionOverrides}
						defaultColor={ColorKeys.headingTextColor}
						elementName={EContentColorOverrideNames.description}
					/>
					{startDate && countdownIsOn ? <Countdown date={startDate} template={template} /> : null}

					<div className="button-holder" style={!startDate ? { marginTop: 18 } : {}}>
						<div className='call-to-action-wrapper'>
							<Button classButton={classNames('primary')} template={template}>
								{content?.callToAction?.button_name?.[language] || content?.callToAction?.button_name?.base}
							</Button>
							<button className="round edit-button" onClick={editButton}><Icon name={ICONS.EDIT} size={13} color={COLORS.WHITE} /></button>
						</div>
						{/* <Button classButton={classNames('watch-video-button secondary', { "no-border": template === TemplateClassNames.O2 })} template={template}>Watch Video</Button> */}
					</div>
				</div>
				<div className="banner-preview-container">
					<div className="header-image-wrapper">
						{
							image
								? <>
									<img src={image.edited} alt="" width="375" height="300" aria-hidden="true" />
									<button className="round edit-button" onClick={openImageModal}><Icon name={ICONS.EDIT} size={13} color={COLORS.WHITE} /></button>
								</>
								: <button className="no-style clear no-image" onClick={openImageModal}>
									<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
									<span>Upload Image</span>
								</button>
						}
					</div>
					{/*Leaving this in case we want to add the play button back to main event banner in the future*/}
					{/* <Button classButton={classNames('play-button primary')} template={template}>
						<EIcon name="play" size="medium" />
					</Button> */}
				</div>

			</div>
			<UploadImageModal
				originalImage={image?.original}
				open={imageModalOpen}
				close={() => setImageModalOpen(false)}
				onSave={saveImage}
			/>
			<Modal
				cancellable={true}
				closeable={false}
				open={callToActionOpen}
				onRequestClose={cancelEditButton}
				title={"Edit button"}
				footer={(
					<>
						<button onClick={cancelEditButton}>Cancel</button>
						<button onClick={setCallToActionButton} className="primary">Done</button>
					</>
				)}
			>
				<CallToActionForm
					onWorkingCTAChange={setWorkingCTA}
					workingCTA={workingCTA || page_module?.content?.callToAction}
					language={language}
					baseLanguage={baseLanguage}
				/>
			</Modal>
		</section>
	) : null;
});

// because we are wrapping the component in the React.forwardRef()
// we need to explicity say what the display name is
EditorMainEventBanner.displayName = 'EditorMainEventBanner';

export default EditorMainEventBanner;